import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useUserRealtimeChannelName } from "@semo/shared-chat";
import {
  SHIPMENT_UPDATED_EVENT,
  invalidateShipmentQueries,
  onChannelJoined,
  parseShipmentUpdated,
  watchShipmentReturn,
} from "@semo/shared-ui";
import {
  getNotificationsControllerGetNotificationsQueryKey,
  getNotificationsControllerGetUnreadCountQueryKey,
} from "@semo/api-contracts";

/**
 * Global Realtime hook for Notifications
 *
 * Subscribes to the supplier's notification channel to receive:
 * 1. New notification events
 * 2. Unread count update events
 * 3. Shipment updated events (송장 상태가 바뀌면 송장 데이터를 담은 쿼리를 다시 읽는다)
 *
 * 채널 이름은 서버가 알려 준 것을 **그대로** 쓴다. 서버는 알림 · 읽지 않은 수 · 배송 신호를 모두 그 이름으로 방송하므로
 * 접미사를 붙이면 아무것도 닿지 않는다(예전에 `_notif` 를 붙여 이 훅의 신호가 통째로 안 왔다).
 * 같은 이름의 채널은 supabase 클라이언트가 하나로 합쳐 주므로 채팅 훅(useGlobalChatRealtime)과 나눠 써도 된다.
 *
 * 연결이 끊긴 동안의 변화는 신호로 오지 않는다 — 채널에 (다시) 들어가면 송장 질의를 한 번 다시 읽고,
 * 창으로 돌아오거나 네트워크가 돌아와도 송장 질의만 다시 읽는다(앱 전역의 refetchOnWindowFocus: false 는 그대로).
 * «들어갔다» 는 `subscribe(콜백)` 의 SUBSCRIBED 가 아니라 가입 응답(`onChannelJoined`)으로 듣는다 —
 * 채팅 훅이 같은 채널에 먼저 들어가 있으면 supabase 는 여기서 넘긴 subscribe 콜백을 등록하지 않는다.
 *
 * 새 알림(new-notification)은 목록 · 배지만 갱신하고 토스트는 띄우지 않는다. 채팅 메시지도 알림 목록에 올라올 수 있어
 * 채팅 토스트(useGlobalChatRealtime)와 겹칠 수 있고, 이 구독이 살아나기 전에는 공급사에게 알림 토스트가 없었다.
 */
export function useGlobalNotificationRealtime(currentUserId?: string) {
  const queryClient = useQueryClient();
  // 채널 이름은 서버가 준다 — 번호로 만들지 않는다(X-01, 이름에 비밀 조각이 붙을 수 있다)
  const channelName = useUserRealtimeChannelName(!!currentUserId);

  // 창 복귀 · 네트워크 복구 때 송장 질의만 다시 읽는다 — 채널 이름을 받기 전에도 켜 둔다
  useEffect(() => {
    if (!currentUserId) return;
    return watchShipmentReturn(queryClient);
  }, [currentUserId, queryClient]);

  useEffect(() => {
    if (!currentUserId || !channelName || !supabase) return;

    console.log(
      `[useGlobalNotificationRealtime] Subscribing to ${channelName}`,
    );

    const channel = supabase
      .channel(channelName)
      .on("broadcast", { event: "new-notification" }, (event) => {
        console.log(
          "[useGlobalNotificationRealtime] New notification received:",
          event,
        );

        // Invalidate queries to refresh notification list and badge
        void queryClient.invalidateQueries({
          queryKey: getNotificationsControllerGetNotificationsQueryKey(),
        });
        void queryClient.invalidateQueries({
          queryKey: getNotificationsControllerGetUnreadCountQueryKey(),
        });
      })
      .on("broadcast", { event: "unread-count-updated" }, (event) => {
        console.log(
          "[useGlobalNotificationRealtime] Unread count updated:",
          event,
        );

        // Invalidate to fetch latest count
        queryClient.invalidateQueries({
          queryKey: getNotificationsControllerGetUnreadCountQueryKey(),
        });
      })
      .on("broadcast", { event: SHIPMENT_UPDATED_EVENT }, (event) => {
        // 모양이 틀린 신호는 무시한다. 값은 신호에 없다 — API 로 다시 읽는다.
        if (!parseShipmentUpdated(event.payload)) return;
        void invalidateShipmentQueries(queryClient);
      });

    // 끊겼다 다시 들어갔을 수 있다 — 그 사이의 신호는 오지 않았으니 송장을 한 번 다시 읽는다
    const stopWatchingJoin = onChannelJoined(channel, () => {
      void invalidateShipmentQueries(queryClient);
    });
    channel.subscribe();

    return () => {
      stopWatchingJoin();
      supabase.removeChannel(channel);
    };
  }, [currentUserId, channelName, queryClient]);
}
